import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import {
    TOP_CONTRIBUTORS_REQUESTED,
    TOP_TAGS_REQUESTED,
    ACTIVITY_CALENDAR_REQUESTED,
    PIECES_COLLECTED_REQUESTED,
} from './constants';
import {
    topContributorsSucceeded,
    topContributorsFailed,
    topTagsSucceeded,
    topTagsFailed,
    activitySucceeded,
    activityFailed,
    piecesCollectedSucceeded,
    piecesCollectedFailed,
} from './actions';
import {
    getTopContributors,
    getTopTags,
    getActivityCalendar,
    getPiecesCollected,
    getMLTags,
} from '../../api';
import { getTaxonomy, getSelectDate, getSelectYear } from './selectors';
import { parseClientError } from '../../api/utils';
import { getUserPermissionRoleId, getUserData } from '../auth/selectors';
import { logException } from '../../logger';

// triggers when user `TOP_CONTRIBUTORS_REQUESTED` action is dispatched
function* fetchTopContributors() {
    try {
        const date = yield select(getSelectDate);
        const topContributors = yield call(getTopContributors, { ...date });
        yield put(topContributorsSucceeded(topContributors));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topContributorsFailed(message));
    }
}

// triggers when user `TOP_TAGS_REQUESTED` action is dispatched
function* fetchTopTags() {
    try {
        const taxonomy = yield select(getTaxonomy);
        // fetch both user and ml tags
        const [userTags, mlTags] = yield all([
            yield call(getTopTags, { taxonomy }),
            yield call(getMLTags, { taxonomy }),
        ]);

        // combine result to update in state
        const result = {
            totalTags:
                (userTags.total ? userTags.total : 0) +
                (mlTags.total ? mlTags.total : 0),
            totalMlTags: mlTags.total ? mlTags.total : 0,
            totalDistilledTags: userTags.total ? userTags.total : 0,
            topTags: {
                userTags: userTags.tags ? userTags.tags : [],
                mlTags: mlTags.tags ? mlTags.tags : [],
            },
        };
        yield put(topTagsSucceeded(result));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topTagsFailed(message));
    }
}

// triggers when user `ACTIVITY_CALENDAR_REQUESTED` action is dispatched
function* fetchActivityCalendar() {
    try {
        const year = yield select(getSelectYear);
        const activityData = yield call(getActivityCalendar, { year });
        yield put(activitySucceeded(activityData));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(activityFailed(message));
    }
}

// triggers when user `PIECES_COLLECTED_REQUESTED` action is dispatched
function* fetchPiecesCollected() {
    try {
        const res = yield call(getPiecesCollected);
        const role = yield select(getUserPermissionRoleId);

        const litterCount = `${role}`.startsWith('3')
            ? res.partnerLitterCount
            : res.litterCount;

        yield put(piecesCollectedSucceeded(litterCount));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(piecesCollectedFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(TOP_CONTRIBUTORS_REQUESTED, fetchTopContributors),
        yield takeLatest(TOP_TAGS_REQUESTED, fetchTopTags),
        yield takeLatest(ACTIVITY_CALENDAR_REQUESTED, fetchActivityCalendar),
        yield takeLatest(PIECES_COLLECTED_REQUESTED, fetchPiecesCollected),
    ]);
}
